import { MainHeader } from "@/pages/layouts/main-header";
import { MainContent } from "@/pages/layouts/main-content";
import { MainHome } from "@/pages/subs/main-home";
import { MainFriends } from "@/pages/subs/main-friends";
import { MainChats } from "@/pages/subs/main-chats";
import { MainNotes } from "@/pages/subs/main-notes";
import { MainPlay } from "@/pages/subs/main-play";
import { MainETC } from "@/pages/subs/main-etc";
import { mainMenu, MAIN_MENUS } from "@/pages/layouts/main-header";
import { useSignals } from "@preact/signals-react/runtime";
import { signal } from "@preact/signals-react";
import { SocketProvider } from "@/providers/socket-provider";
import { ChatProvider } from "@/providers/chat-provider";
import { FriendManager } from "@/pages/subs/friends/friend-manager";
import { SharedDataManager } from "@/features/shared-data-manager";
import { NoteProvider } from "@/providers/note-provider";
import { useEffect, useState } from "react";
import { fetchGistData } from "@/lib/client-api";
import { pwaStatus } from "@/features/pwa-controller";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/providers/language-provider";

export const isMaintaining = signal(false);
export const maintenanceMessage = signal("");

// 하이브리드 방식 설정
// const PERSISTENT_TABS = [MAIN_MENUS.CHATS] as const;
const LAZY_TABS = [MAIN_MENUS.NOTES, MAIN_MENUS.PLAY] as const;

export default function MainPage() {
  useSignals();
  const { t } = useTranslation();

  // Lazy 탭들의 로드 상태 관리
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set());

  // 탭 전환시 필요한 경우 로드
  useEffect(() => {
    const currentTab = mainMenu.value;
    if (LAZY_TABS.includes(currentTab as any) && !loadedTabs.has(currentTab)) {
      setLoadedTabs((prev) => new Set([...prev, currentTab]));
    }
  }, [mainMenu.value, loadedTabs]);

  const renderPersistentTabs = () => {
    return (
      <>
        {/* 항상 마운트되는 중요한 탭들 */}
        <MainChats hidden={mainMenu.value !== MAIN_MENUS.CHATS} />
      </>
    );
  };

  const renderLazyTabs = () => {
    return (
      <>
        {/* 필요시에만 로드되는 탭들 */}
        {loadedTabs.has(MAIN_MENUS.NOTES) && (
          <MainNotes hidden={mainMenu.value !== MAIN_MENUS.NOTES} />
        )}
        {loadedTabs.has(MAIN_MENUS.PLAY) && (
          <MainPlay hidden={mainMenu.value !== MAIN_MENUS.PLAY} />
        )}
      </>
    );
  };

  const renderCurrentTab = () => {
    // 매번 새로 렌더링되는 탭들
    if (mainMenu.value === MAIN_MENUS.HOME) {
      return <MainHome />;
    }
    if (mainMenu.value === MAIN_MENUS.FRIENDS) {
      return <MainFriends />;
    }
    if (mainMenu.value === MAIN_MENUS.ETC) {
      return <MainETC />;
    }

    // 하이브리드 방식으로 처리되는 탭들은 별도로 렌더링됨
    return null;
  };

  const fetchMaintenance = async () => {
    const data = await fetchGistData("eeb6721781c7f4b939d252d84a24dc3d");
    if (data.files["Maintenance"]) {
      const content = data.files["Maintenance"].content;
      maintenanceMessage.value = content.trim();
      isMaintaining.value = content.trim() !== "";
    }
  };

  useEffect(() => {
    fetchMaintenance();
  }, []);

  const updatePWA = async () => {
    console.log("updatePWA");
    const { updateSW } = pwaStatus.value || {};
    // updateSW?.(false)
    //   .then(() => {
    //     location.replace(location.pathname);
    //   })
    //   .catch((error) => {
    //     console.error("[PWA] Update failed:", error);
    //   });

    if (updateSW) {
      // 업데이트 버튼의 사용자 반응을 즉시 반영
      pwaStatus.value.needsRefresh = false;

      // 브라우저가 UI를 그릴 시간을 벌어줌 (이벤트 루프의 다음 틱으로 미룸)
      setTimeout(async () => {
        try {
          // 1. 제어권이 변경되었을 때 새로고침하는 리스너를 미리 등록
          navigator.serviceWorker.addEventListener(
            "controllerchange",
            () => {
              // 실제로 서비스 워커가 교체된 시점에 새로고침
              window.location.reload();
            },
            { once: true },
          ); // 중요: 한 번만 실행

          // 2. 서비스 워커에게 업데이트 명령 (skipWaiting 실행)
          // 여기서 전달하는 인자는 'reload' 여부인데,
          // 위에서 리스너를 등록했으므로 false를 주거나 호출만 해도 됩니다.
          await updateSW(false);

          // 안전장치
          setTimeout(() => window.location.reload(), 1500);
        } catch (error) {
          console.error("PWA 업데이트 중 오류 발생:", error);
          // 에러 발생 시 사용자에게 다시 기회를 주기 위해 상태 복구
          pwaStatus.value.needsRefresh = true;
        }
      }, 50);
    } else {
      console.error("updateSW 함수가 정의되지 않았습니다.");
    }
  };

  return (
    <SocketProvider>
      <ChatProvider>
        <NoteProvider>
          <SharedDataManager />
          <FriendManager />
          <div className="flex flex-col w-[100dvw] h-[var(--vh,100dvh)] overflow-hidden">
            <MainContent>
              {/* 일반 탭 (매번 새로 렌더링) */}
              {renderCurrentTab()}

              {/* 항상 마운트되는 중요한 탭들 */}
              {renderPersistentTabs()}

              {/* 필요시에만 로드되는 탭들 */}
              {renderLazyTabs()}
            </MainContent>
            <MainHeader />
            {pwaStatus.value?.needsRefresh ? (
              <Button
                className="fixed top-1 left-1/2 -translate-x-1/2 w-[50vw]"
                variant="destructive"
                onClick={updatePWA}
              >
                {t("Update")}
              </Button>
            ) : null}
          </div>
        </NoteProvider>
      </ChatProvider>
    </SocketProvider>
  );
}
